import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LogOut, AlertCircle, Sparkles, ArrowLeft, Trash2, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Toaster, toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGender } from "../lib/gender";
import { ClientAdminSidebar } from "../components/ClientAdminSidebar";
import { UndoRedoFab } from "./fullframe/UndoStack";
import { ClientBriefWizard } from "../components/ClientBriefWizard";
import { extractDominantColor, darken, lighten } from "../lib/colorExtract";

/**
 * ClientDashboard — landing page for the customer's white-label dashboard
 * at /{slug}. Shows a branded "ברוך הבא" hero, the colour-extracted brand,
 * and a single CTA that opens the 5-step business brief questionnaire.
 */
export default function ClientDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [, , t] = useGender();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 5-step business brief popup
  const [briefOpen, setBriefOpen] = useState(false);
  // Permanent account deletion confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Dominant colour extracted from the uploaded company logo (used as a
  // primary brand accent on this page).
  const [brand, setBrand] = useState("#7c3aed");

  useEffect(() => {
    if (!company?.logo_url) { setBrand("#7c3aed"); return; }
    let alive = true;
    extractDominantColor(company.logo_url).then((c) => { if (alive) setBrand(c); });
    return () => { alive = false; };
  }, [company?.logo_url]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await api.get(`/companies/by-slug/${slug}`);
        if (alive) setCompany(data);
      } catch (e) {
        if (alive) setError(
          e.response?.status === 403 ? "אין לך הרשאה לדשבורד הזה"
          : (e.response?.data?.detail || "החברה לא נמצאה")
        );
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Owner trying to access another company's dashboard? push them out.
  useEffect(() => {
    if (!user || !company) return;
    if (user.role === "owner" && user.company_id !== company.id) {
      toast.error("אין לך הרשאה לדשבורד הזה");
      navigate("/login", { replace: true });
    }
  }, [user, company, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" dir="rtl">
      <p className="text-stone-500">טוען...</p>
    </div>
  );

  if (error || !company) return (
    <div className="min-h-screen flex items-center justify-center px-4" dir="rtl">
      <div className="max-w-md text-center space-y-4">
        <AlertCircle className="h-12 w-12 text-rose-500 mx-auto" />
        <h1 className="font-heading text-xl font-bold">{error || "לא נמצא"}</h1>
        <Button variant="outline" onClick={() => navigate("/login")}>חזרה לכניסה</Button>
      </div>
    </div>
  );

  // Manager's name (contact_name) — used in the welcome message.
  const managerName = (company.contact_name || company.name || "").split(/\s+/)[0];
  const briefDone = !!company.brief?.finished;

  const brandSoft = lighten(brand, 0.9);
  const brandDark = darken(brand, 0.18);
  const pageStyle = {
    background: `linear-gradient(135deg, ${brandSoft} 0%, #ffffff 50%, ${brandSoft} 100%)`,
    ["--brand"]: brand,
    ["--brand-dark"]: brandDark,
    ["--brand-soft"]: brandSoft,
  };

  const handleDeleteAccount = async () => {
    setDeleteBusy(true);
    try {
      await api.delete("/users/me");
      try {
        localStorage.removeItem("flowup_auth_token");
        sessionStorage.removeItem("flowup_auth_token");
      } catch {
        /* no-op */
      }
      toast.success("החשבון נמחק לחלוטין. להתראות 👋");
      setDeleteOpen(false);
      setTimeout(() => navigate("/", { replace: true }), 800);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "מחיקת החשבון נכשלה — נסה שוב");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={pageStyle} dir="rtl">
      <Toaster position="top-center" richColors dir="rtl" />

      {/* Header — white-label */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-20" style={{ borderColor: brand + "33" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {company.logo_url ? (
              <div className="h-12 w-12 rounded-xl bg-white border-2 overflow-hidden flex items-center justify-center shadow-sm shrink-0" style={{ borderColor: brand + "55" }}>
                <img src={company.logo_url} alt={company.name} className="h-full w-full object-contain p-1" data-testid="client-logo" />
              </div>
            ) : (
              <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-2xl shrink-0" style={{ background: `linear-gradient(135deg, ${brand}, ${brandDark})` }}>
                {(company.name || "?").trim().charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-lg sm:text-xl truncate">{company.display_name || company.name}</h1>
              <p className="text-xs text-stone-500 truncate">דשבורד אישי</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1 text-xs text-rose-500 hover:text-rose-700 transition"
              data-testid="client-delete-account-btn"
              title="מחיקת חשבון לחלוטין"
            >
              <Trash2 className="h-3.5 w-3.5" />
              מחק חשבון
            </button>
            <span className="text-stone-300 select-none">·</span>
            <button
              type="button"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-rose-600 transition"
              data-testid="client-logout"
            >
              <LogOut className="h-3.5 w-3.5" />
              יציאה
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-10">
        {/* Greeting — "ברוך הבא [שם המנהל]" + CTA "איך נוכל להתחיל לעבוד?" */}
        <div className="text-center" data-testid="greeting-block">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5" style={{ color: brand }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: brandDark }}>{company.display_name || company.name}</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 leading-tight">
            {t("ברוך הבא", "ברוכה הבאה", "שלום")}{managerName ? `, ${managerName}` : ""} 👋
          </h2>
          <p className="mt-3 text-lg sm:text-xl font-semibold" style={{ color: brandDark, display: company.brief?.finished ? "none" : undefined }}>
            איך נוכל להתחיל לעבוד?
          </p>

          {/* Brief approval indicator */}
          {company.brief?.finished && company.brief_approved && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border-2 border-emerald-300 px-3 py-1 text-xs font-extrabold text-emerald-700" data-testid="brief-approved-badge">
              <Check className="h-3.5 w-3.5" />
              האפיון שלך אושר ✓
            </div>
          )}
          {company.brief?.finished && !company.brief_approved && (
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border-2 border-amber-300 px-3 py-1 text-xs font-extrabold text-amber-700" data-testid="brief-pending-badge">
              ⏳ האפיון בבדיקה — נחזור אליך בקרוב
            </div>
          )}
        </div>

        {/* Brief CTA — single colourful card that opens the multi-step
            questionnaire so the client can fully describe their business. */}
        <div className="max-w-2xl mx-auto" data-testid="brief-cta-wrap">
          <button
            type="button"
            onClick={() => setBriefOpen(true)}
            className="group relative w-full overflow-hidden rounded-3xl p-7 sm:p-9 shadow-2xl hover:scale-[1.02] active:scale-[0.99] transition-all"
            style={{ background: `linear-gradient(135deg, ${brand} 0%, ${brandDark} 100%)` }}
            data-testid="open-brief-btn"
          >
            <div className="absolute -top-16 -left-16 h-56 w-56 rounded-full bg-white/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-white/10 blur-2xl pointer-events-none" />
            <div className="absolute top-4 left-4 text-4xl opacity-70 group-hover:scale-125 group-hover:rotate-12 transition" aria-hidden>✨</div>
            <div className="absolute bottom-4 right-4 text-3xl opacity-60 group-hover:scale-125 group-hover:-rotate-12 transition" aria-hidden>🎨</div>

            <div className="relative text-center sm:text-right">
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold text-white uppercase tracking-widest mb-3" data-testid="brief-status-pill">
                {briefDone ? "אפיון נשמר ✓" : "5 שלבים · ~3 דקות"}
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white drop-shadow leading-tight">
                {briefDone
                  ? t("ערוך את אפיון העסק שלך", "ערכי את אפיון העסק שלך")
                  : t("בוא נכיר את העסק שלך", "בואי נכיר את העסק שלך")}
              </h3>
              <p className="mt-2 text-sm sm:text-base text-white/85 max-w-md">
                {briefDone
                  ? "אנחנו שומרים את כל המידע שמילאת — אפשר תמיד להוסיף ולערוך."
                  : "ענה על שאלון קליל וצבעוני, אנחנו נבנה לך כלים מותאמים אישית 🚀"}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 bg-white text-stone-900 font-extrabold rounded-full px-5 py-2.5 text-sm shadow-md">
                {briefDone ? "המשך עריכה" : "התחל אפיון"}
                <ArrowLeft className="h-4 w-4" />
              </span>
            </div>
          </button>
        </div>
      </main>

      {/* Admin sidebar — credits, addons, vault. Visible to owner + super-admin. */}
      <ClientAdminSidebar company={company} />

      {/* Floating undo/redo controls — top-left corner, vertical. */}
      <div className="fixed top-20 left-4 z-[10100]">
        <UndoRedoFab />
      </div>
      {/* Client business brief — colourful 5-step questionnaire. */}
      <ClientBriefWizard
        companyId={company?.id}
        company={company}
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        onOpenTopup={() => {
          // Defer so the wizard fully unmounts first, then let the admin
          // sidebar pick up the request.
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("flowup:open-topup"));
          }, 200);
        }}
        onSaved={async () => {
          try {
            const { data: r } = await api.get(`/companies/by-slug/${slug}`);
            if (r) setCompany(r);
          } catch {}
        }}
      />
      {/* Permanent account deletion confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent dir="rtl" data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-rose-600" />
              מחיקת חשבון לחלוטין
            </AlertDialogTitle>
            <AlertDialogDescription className="text-right text-sm leading-relaxed">
              פעולה זו תמחק לצמיתות את העסק שלך, כל הנתונים, ההיסטוריה, היתרה,
              ההפקות והמשתמשים המשויכים. <strong>לא ניתן לשחזר את המידע</strong>.
              להמשיך?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
              data-testid="confirm-delete-account-btn"
            >
              {deleteBusy ? "מוחק..." : "כן, מחק לצמיתות"}
            </AlertDialogAction>
            <AlertDialogCancel disabled={deleteBusy} data-testid="cancel-delete-account-btn">
              ביטול
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
